import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { MessageType, SessionAction, AbstractTranslatableSnackBarService } from "@obg/ngx.core";
import { ImageSelector, TranslationGroup, TranslationService } from "@obg/ngx.core.content";
import { CacheFactory, CacheStore, fromMillisecondsToMinutes, StorageMode, StoreContainer } from "@obg/ngx.global";
import { Observable } from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";

import { HardSessionConfigService } from "./hard-session.config";
import { HARD_SESSION_LIMIT_KEYS } from "./hard-session.const";

const SESSION_DURATION_CACHE_KEY = "sessionDuration";
const CACHE_STORE_KEY = "common.responsible-gaming";

@Injectable({
	providedIn: "root",
})
export class HardSessionService {
	private cache: CacheStore;
	private isAlertShown = false;

	constructor(
		private cacheFactory: CacheFactory,
		private snackBarService: AbstractTranslatableSnackBarService,
		private store: Store,
		private imageSelector: ImageSelector,
		private config: HardSessionConfigService,
		private sessionAction: SessionAction,
		private translationService: TranslationService
	) {
		this.cache = this.cacheFactory.get(CACHE_STORE_KEY, {
			storageMode: StorageMode.LocalStorage,
			maxAge: Number.MAX_VALUE,
		});
	}

	get getIsAlertShown() {
		return this.isAlertShown;
	}

	set setIsAlertShown(_isAlertShown: boolean) {
		this.isAlertShown = _isAlertShown;
	}

	checkRemainingHardSessionDuration$(): Observable<number> {
		return this.cache.getObjectAsObservable<any>(SESSION_DURATION_CACHE_KEY).pipe(
			filter((sessionInfo) => !!sessionInfo?.created),
			map((sessionInfo: StoreContainer<number>) => {
				const currentTime = Math.round(new Date().getTime());
				const hardSessionExpiry = sessionInfo?.created + this.config.getHardSessionLimitDuration();

				const remainingMinutes = fromMillisecondsToMinutes(hardSessionExpiry - currentTime);
				if (remainingMinutes <= 0) {
					this.store.dispatch(this.sessionAction.logout());
				}

				return hardSessionExpiry - currentTime;
			})
		);
	}

	showHardSessionNotification$(hardSessionMsDuration: number): Observable<void> {
		const durationInMinutes = fromMillisecondsToMinutes(hardSessionMsDuration);
		const duration =
			durationInMinutes < 1
				? this.translationService.getByKey(HARD_SESSION_LIMIT_KEYS.lessThanMinute)
				: `${Math.round(durationInMinutes)} ${this.translationService.getByKey(
						HARD_SESSION_LIMIT_KEYS.minutes
					)}`;

		return this.store.pipe(
			select(this.imageSelector.getByKey("general", HARD_SESSION_LIMIT_KEYS.infoIcon)),
			switchMap((icon) =>
				this.snackBarService.openSnackBar$(
					{
						message: HARD_SESSION_LIMIT_KEYS.hgcHardSessionLimitMessage,
						dismissible: true,
						duration: 20000,
						icon,
						type: MessageType.Info,
					},
					[TranslationGroup.Common],
					{ duration }
				)
			),
			tap(() => (this.setIsAlertShown = true)),
			map(() => undefined)
		);
	}
}
